import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Container,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Logout as LogoutIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material';
import { ROLE_COLORS } from '../../constants';

const HOST_KEY = 'host';

export default function SelectOrganisation() {
  const { user, switchOrg, logout } = useAuth();
  const [submitting, setSubmitting] = useState(null);
  const [error, setError] = useState('');

  // key: an organisationId, or HOST_KEY for the platform context (organisationId = null).
  const handleSelect = async (key) => {
    setSubmitting(key);
    setError('');
    const result = await switchOrg(key === HOST_KEY ? null : key);
    if (!result.success) {
      setError(result.error || 'Failed to switch organisation.');
      setSubmitting(null);
    }
  };

  const memberships = user?.memberships || [];
  const canActAsHost = user?.canActAsHost === true;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', bgcolor: 'background.default', py: 6 }}>
      <Container maxWidth="sm">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" gutterBottom>Select an organisation</Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome back, {user?.firstName}. Choose which organisation you want to work in.
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {canActAsHost && (
            <Card variant="outlined">
              <CardActionArea
                disabled={submitting != null}
                onClick={() => handleSelect(HOST_KEY)}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ShieldIcon color="primary" />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" noWrap fontWeight={600}>
                      Host Administration
                    </Typography>
                    <Chip
                      label="Host"
                      color={ROLE_COLORS.Host || 'secondary'}
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                  {submitting === HOST_KEY && <CircularProgress size={20} />}
                </CardContent>
              </CardActionArea>
            </Card>
          )}
          {memberships.map((m) => (
            <Card key={m.organisationId} variant="outlined">
              <CardActionArea
                disabled={submitting != null}
                onClick={() => handleSelect(m.organisationId)}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <BusinessIcon color="primary" />
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" noWrap fontWeight={600}>
                      {m.organisationName}
                    </Typography>
                    <Chip
                      label={m.role}
                      color={ROLE_COLORS[m.role] || 'default'}
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.5 }}
                    />
                  </Box>
                  {submitting === m.organisationId && <CircularProgress size={20} />}
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>

        {memberships.length === 0 && !canActAsHost && (
          <Alert severity="warning">
            Your account is not associated with any organisation. Please contact your administrator.
          </Alert>
        )}

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button startIcon={<LogoutIcon />} onClick={logout} disabled={submitting != null}>
            Sign out
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
